import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import moment from 'moment';
import 'moment-timezone';
import { useLocation, useNavigate } from 'react-router-dom';
import { I18n } from 'react-redux-i18n';
import get from 'lodash.get';

// Lucide React icons
import {
  Calendar, TrendingUp, ChevronDown,
  DollarSign, ShoppingCart, Ticket, Users, Heart, Settings,
  RefreshCw, Edit3, ExternalLink, Copy, UserCheck, UserPlus, History, QrCode, LayoutDashboard
} from 'lucide-react';

// Components
import { AnalyticsLineChart } from '../../../components/charts';
import { ComparisonControls } from '../../../components/charts/ComparisonControls';
import DatePickerInput from '../../../components/form/datePicker';
import BasePage from '../../../components/common/basePageView';
import Spinner from '../../../components/common/spinner';
import { DonationListWidget } from '../../../components/common/DonationListWidget';
import { FundraiserListWidget } from '../../../components/common/FundraiserListWidget';
import { MetricCard } from '../../../components/common/MetricCard';
import { GiftMatchWidget, type GiftMatch } from '../../../components/common/GiftMatchWidget';
import { TicketingSalesWidget } from '../../../components/common/TicketingSalesWidget';
import { formatTimeElapsedOrDate } from '../../../utils/time/formatTimeElapsed';
import {
  EntityHeaderActions
} from '../../../components/common/EntityHeaderActions/EntityHeaderActions';
import type { EntityActionsMenuItem } from '../../../components/common/EntityActionsDropdown/EntityActionsDropdown';
import { CampaignLinksModal } from '../../../components/common/CampaignLinksModal';
import { SystemOnlyDetail } from '../../../components/common/SystemOnlyBadge';
import { EntityTimestamps, entityStatus } from './EntityTimestamps';

// Hooks
import { usePageTitle, useSession } from '../../../hooks';
import { isReadWrite, type Session } from '../../../modules/session';

// Actions and helpers
import * as dashboardActions from '../../dashboard/actions';
import { dashboardViewTypeConfigurations, reportTimelineBracket } from '../../dashboard/constants';
import currencyHelper from '../../../helpers/currencyHelper';
import config from '../../../constants';
import { Record } from '../../../modules/bin/utility';
import { getDashboardCreatedAt } from '../../dashboard/helper';
import type {
  ComparableEntity,
  ComparisonDataRecordsMap,
  EntityModel,
  EntityPerformanceProps,
  GiftMatchingCondition,
  GiftMatching,
  ListFilterPrimitive,
  ListRequestFilter,
  ListRequest,
  SummaryDataApiResponse,
  VisibleEntitiesMap
} from './types';

// Styles
import styles from '../../campaign/performance/CampaignPerformance.module.css';
import { FranchiseEditionType } from '../../franchise/hub/types';
import { CONTEXT_MENU_KEYS } from '../../../constants/contextMenuEntries';

const createDefaultVisibleMetrics = () => ({
  raisedAmount: true,
  donationAmount: true,
  saleAmount: true,
  numberOfSoldTickets: true,
  numberOfActivePages: true,
  numberOfDonations: true,
  numberOfFundraisingPages: false
});

type MetricVisibilityKey = keyof ReturnType<typeof createDefaultVisibleMetrics>;

// Chart colors for metrics and entity comparison
const CHART_COLORS = [
  '#FF5642', // Red-orange (Total raised)
  '#FF9842', // Orange (Donations)
  '#42A0FF', // Blue (Sales)
  '#424EFF', // Deep blue (Tickets)
  '#17BECF', // Cyan (Active pages)
  '#00CC8F', // Green (Number of donations)
  '#8442FF'  // Purple (Fundraising pages)
];

// Extended palette for comparison mode only (10 colors total).
// Requirement: the FIRST color must always be #FF5642; subsequent colors are
// chosen to maximize contrast and avoid near-duplicates of the first.
const COMPARISON_MODE_PALETTE: string[] = [
  '#FF5642', // anchor (current entity)
  '#FF42DC', // hot pink
  '#D342FF', // violet
  '#9467BD', // lavender
  '#00CC8F', // mint green
  '#8442FF', // indigo
  '#42A0FF', // sky blue
  '#FF9842', // amber
  '#66CC00', // lime green
  '#0044CC'  // royal blue
];

// Helper to pick comparison colors. Index 0 is always #FF5642.
// Optional override palette can be passed; index 0 remains the anchor color.
const getComparisonColor = (index: number, override?: string[]): string => {
  if (index === 0) return '#FF5642';
  const palette = Array.isArray(override) && override.length >= 2
    ? override
    : COMPARISON_MODE_PALETTE;
  // skip index 0 since it's reserved for #FF5642 already returned above
  const effectiveIndex = index % palette.length;
  return palette[effectiveIndex];
};

type TimePeriodKey = '7days' | '30days' | 'allTime' | 'custom';

type QueryParamUpdates = {
  period?: TimePeriodKey | null;
  from?: string | null;
  to?: string | null;
  comparison?: boolean | null;
  timeBracket?: string | null;
  trim?: boolean | null;
};

const appendAlpha = (hex: string, alphaHex = '1a') => {
  const value = (hex || '').trim();
  if (!value || !value.startsWith('#')) {
    return hex;
  }
  if (value.length === 4) {
    const r = value[1];
    const g = value[2];
    const b = value[3];
    return `#${r}${r}${g}${g}${b}${b}${alphaHex}`;
  }
  if (value.length === 7) {
    return `${value}${alphaHex}`;
  }
  if (value.length === 9) {
    return value;
  }
  return hex;
};

// Same roles as the /campaigns/:campaignId/check-in route, which redirects everyone else
const canUseCheckIn = (session: Session) =>
  (session.isSystemAdmin && isReadWrite(session)) ||
  session.isOrganizationAdmin ||
  session.isCampaignAdmin;

const EntityPerformancePage: React.FC<EntityPerformanceProps> = (props) => {
  const {
    entityId,
    entityType,
    inited,
    modelRecord,
    summaryRecord,
    summaryDataRecord,
    previousSummaryRecord,
    timePeriod,
    timeBracket,
    summaryType,
    reportByDateAndMetricType,
    configurations,
    isSystemAdmin,
    isEventAdmin,
    isEventOrganizerAdmin,
    init,
    getSummaryRecord,
    getSummaryDataRecord,
    onValueChange,
    getRecord,
    topDonationsList,
    topFundraisersList,
    getTopDonationsListData,
    getTopFundraisersListData,
    ticketingSales
  } = props;

  const location = useLocation();
  const navigate = useNavigate();

  const urlQueryState = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    const rawPeriod = (params.get('period') || '').toLowerCase();

    let periodKey: TimePeriodKey = '7days';
    if (rawPeriod === '30days') {
      periodKey = '30days';
    } else if (rawPeriod === 'alltime' || rawPeriod === 'allTime') {
      periodKey = 'allTime';
    } else if (rawPeriod === 'custom') {
      periodKey = 'custom';
    }

    return {
      periodKey,
      customFromParam: params.get('from') || '',
      customToParam: params.get('to') || '',
      comparisonEnabled: params.get('compare') === 'true',
      timeBracketParam: params.get('timeBracket') || '',
      trimEnabled: params.get('trim') === 'true'
    };
  }, [location.search]);

  const writeQueryParams = useCallback(
    (updates: QueryParamUpdates) => {
      try {
        const params = new URLSearchParams(location.search || '');
        const originalQuery = params.toString();

        if (updates.period !== undefined) {
          if (!updates.period || updates.period === '7days') {
            params.delete('period');
          } else {
            params.set('period', updates.period);
          }
        }

        if (updates.from !== undefined) {
          if (updates.from) {
            params.set('from', updates.from);
          } else {
            params.delete('from');
          }
        }

        if (updates.to !== undefined) {
          if (updates.to) {
            params.set('to', updates.to);
          } else {
            params.delete('to');
          }
        }

        if (updates.comparison !== undefined) {
          if (updates.comparison) {
            params.set('compare', 'true');
          } else {
            params.delete('compare');
          }
        }

        if (updates.timeBracket !== undefined) {
          if (updates.timeBracket) {
            params.set('timeBracket', updates.timeBracket);
          } else {
            params.delete('timeBracket');
          }
        }

        if (updates.trim !== undefined) {
          if (updates.trim) {
            params.set('trim', 'true');
          } else {
            params.delete('trim');
          }
        }

        const nextQuery = params.toString();
        if (nextQuery === originalQuery) {
          return;
        }

        const nextSearch = nextQuery ? `?${nextQuery}` : '';
        navigate(
          {
            pathname: location.pathname,
            search: nextSearch
          },
          { replace: true }
        );
      } catch (error) {
        // noop
      }
    },
    [location.pathname, location.search, navigate]
  );

  const [selectedLiveIndex, setSelectedLiveIndex] = useState(0);
  const [selectedNonLiveIndex, setSelectedNonLiveIndex] = useState(0);
  const [selectedMetric, setSelectedMetric] = useState('raisedAmount');
  const [showMetricSettings, setShowMetricSettings] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [timePeriodLocal, setTimePeriodLocal] = useState('7days');
  const [customFrom, setCustomFrom] = useState<string>('');
  const [customTo, setCustomTo] = useState<string>('');
  const [draftCustomFrom, setDraftCustomFrom] = useState<string>('');
  const [draftCustomTo, setDraftCustomTo] = useState<string>('');
  const [isCustomRangeOpen, setIsCustomRangeOpen] = useState(false);
  const [includeOffline, setIncludeOffline] = useState(true);
  const [selectedFundraiserType, setSelectedFundraiserType] = useState<string>('fundraiser');
  const [donationListMode, setDonationListMode] = useState<'top' | 'recent'>('top');
  const [visibleMetrics, setVisibleMetrics] = useState(createDefaultVisibleMetrics);

  const [isComparisonMode, setIsComparisonMode] = useState(false);
  const [comparableEntities, setComparableEntities] = useState<ComparableEntity[]>([]);
  const [isLoadingComparableEntities, setIsLoadingComparableEntities] = useState(false);
  const [comparisonDataRecords, setComparisonDataRecords] = useState<ComparisonDataRecordsMap>({});
  const [isLoadingComparisonData, setIsLoadingComparisonData] = useState(false);
  const [visibleEntities, setVisibleEntities] = useState<VisibleEntitiesMap>({});
  const [urlStateReady, setUrlStateReady] = useState(false);
  
  // Loading states for leaderboard tabs
  const [isLoadingDonations, setIsLoadingDonations] = useState(false);
  const [isLoadingFundraisers, setIsLoadingFundraisers] = useState(false);
  const [campaignLinksModalOpen, setCampaignLinksModalOpen] = useState(false);
  
  // Trim empty leading data state with localStorage persistence (default: enabled)
  const [trimEmptyData, setTrimEmptyData] = useState(() => {
    try {
      const stored = localStorage.getItem('entityPerformance.trimEmptyData');
      return stored === null ? true : stored === 'true';
    } catch {
      return true;
    }
  });

  const hasValidTimePeriod = Boolean(timePeriod?.dateFrom && timePeriod?.dateTo);
  const entityLoadedForComparison = useMemo(() => {
    const recordId = get(modelRecord, 'data.data.id');
    if (recordId === undefined || recordId === null) {
      return false;
    }
    return String(recordId) === String(entityId ?? '');
  }, [modelRecord, entityId]);

  const settingsRef = useRef<HTMLDivElement | null>(null);
  const customRangeRef = useRef<HTMLDivElement | null>(null);
  const previousTimePeriodRef = useRef<'7days' | '30days' | 'allTime'>('7days');
  const ticketingMetricsAutoHiddenRef = useRef(false);
  const loadDataCalledRef = useRef(false);
  const comparisonFetchSignatureRef = useRef<string>('');

  useEffect(() => {
    setVisibleMetrics(createDefaultVisibleMetrics());
    setSelectedMetric('raisedAmount');
    ticketingMetricsAutoHiddenRef.current = false;
  }, [entityId]);

  useEffect(() => {
    setUrlStateReady(false);
  }, [entityId]);

  useEffect(() => {
    if (!entityId) {
      setUrlStateReady(false);
      return;
    }

    const requiresTimePeriod = !isComparisonMode;
    const comparisonReady = !isComparisonMode || entityLoadedForComparison;
    const hasRequiredRange = !requiresTimePeriod || hasValidTimePeriod;
    const nextReady = comparisonReady && hasRequiredRange;

    setUrlStateReady(prev => (prev === nextReady ? prev : nextReady));
  }, [entityId, isComparisonMode, entityLoadedForComparison, hasValidTimePeriod]);

  useEffect(() => {
    if (!showMetricSettings) return;
    const handleDocMouseDown = (e: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target as Node)) {
        setShowMetricSettings(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowMetricSettings(false);
      }
    };
    document.addEventListener('mousedown', handleDocMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleDocMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showMetricSettings]);


  useEffect(() => {
    if (!isCustomRangeOpen) return;

    const handleDocMouseDown = (e: MouseEvent) => {
      if (customRangeRef.current && !customRangeRef.current.contains(e.target as Node)) {
        setDraftCustomFrom(customFrom || '');
        setDraftCustomTo(customTo || '');
        setIsCustomRangeOpen(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setDraftCustomFrom(customFrom || '');
        setDraftCustomTo(customTo || '');
        setIsCustomRangeOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocMouseDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleDocMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isCustomRangeOpen, customFrom, customTo]);

  useEffect(() => {
    if (!isCustomRangeOpen) {
      setDraftCustomFrom(customFrom || '');
      setDraftCustomTo(customTo || '');
    }
  }, [customFrom, customTo, isCustomRangeOpen]);

  useEffect(() => {
    if (timePeriodLocal !== 'custom' && isCustomRangeOpen) {
      setIsCustomRangeOpen(false);
    }
  }, [timePeriodLocal, isCustomRangeOpen]);

  useEffect(() => {
    if (timePeriodLocal === 'custom') return;
    previousTimePeriodRef.current = timePeriodLocal as '7days' | '30days' | 'allTime';
  }, [timePeriodLocal]);

  const updateUrlPeriod = (
    rangeKey: TimePeriodKey,
    fromStr?: string,
    toStr?: string
  ) => {
    if (rangeKey === '7days') {
      writeQueryParams({ period: null, from: null, to: null });
      return;
    }

    if (rangeKey === 'custom') {
      if (fromStr && toStr) {
        writeQueryParams({ period: 'custom', from: fromStr, to: toStr });
      } else {
        writeQueryParams({ period: 'custom', from: null, to: null });
      }
      return;
    }

    writeQueryParams({ period: rangeKey, from: null, to: null });
  };

  const updateUrlTimeBracket = (tb: string) => {
    if (!tb || tb === reportTimelineBracket.Default) {
      writeQueryParams({ timeBracket: null });
      return;
    }

    writeQueryParams({ timeBracket: tb });
  };

  // Resolve the organisation currency for this entity so metrics and the chart
  // use its symbol instead of defaulting to AUD ($). The summary carries it on
  // some responses; otherwise fall back to the entity, then its organization.
  const currencyCode =
    (get(summaryRecord, 'data.data.currency') ||
      get(modelRecord, 'data.data.currency') ||
      get(modelRecord, 'data.data.organization.currency') ||
      'AUD') as string;

  const formatCurrency = (amount: number, currency = currencyCode) => {
    return currencyHelper.formatCurrency(amount, currency);
  };

  const renderCurrencyCompact = (amount: number, currency = currencyCode) => {
    const formatted = formatCurrency(amount, currency);
    const m = formatted.match(/^(.*?)([\.,])(\d{2})$/);
    if (!m) return formatted;
    const [, major, sep, cents] = m;
    return (
      <>
        {major}
        <span className={styles.metricValueMinor}>{`${sep}${cents}`}</span>
      </>
    );
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  const formatFundraiserType = (rawType: any) => {
    const s = (rawType ?? '').toString().toLowerCase();
    if (s === 'team') return 'Teams';
    if (s === 'fundraiser') return 'Individuals';
    if (s === 'collection') return 'Superteams';
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
  };

  const deriveEntityKey = (value: unknown): string | null => {
    if (value === null || value === undefined) {
      return null;
    }
    const key = String(value).trim();
    return key.length > 0 ? key : null;
  };

  const resolveComparableName = (candidate: ComparableEntity): string => {
    const rawName = get(candidate, 'franchiseEdition.name', get(candidate, 'name', ''));
    if (typeof rawName === 'string') {
      return rawName;
    }
    if (rawName === null || rawName === undefined) {
      return '';
    }
    return String(rawName);
  };

  // Try to resolve a 4-digit year from common fields; fallback to start date or name parsing.
  const getComparableYearNumber = (candidate: ComparableEntity): number => {
    const tryFields: Array<any> = [
      get(candidate, 'franchiseEditionYear'),
      get(candidate, 'franchiseEdition.year'),
    ];

    for (const val of tryFields) {
      if (val !== undefined && val !== null) {
        const n = Number(String(val).match(/(19|20)\d{2}/)?.[0] ?? val);
        if (!Number.isNaN(n) && n > 0) return Math.trunc(n);
      }
    }

    const startIso = get(candidate, 'timeAndPlace.time.start') ?? get(candidate, 'startDate');
    if (typeof startIso === 'string') {
      const d = new Date(startIso);
      const y = d.getUTCFullYear();
      if (!Number.isNaN(y) && y > 0) return y;
    }

    const nameStr = resolveComparableName(candidate);
    const match = nameStr.match(/(19|20)\d{2}/);
    if (match) return Number(match[0]);

    return 0;
  };

  const normaliseComparableResponse = (
    rawList: unknown,
    currentEntityKey: string | null
  ): ComparableEntity[] => {
    if (!Array.isArray(rawList)) {
      return [];
    }

    return (rawList as ComparableEntity[]) 
      .filter((candidate) => {
        const candidateKey = deriveEntityKey(get(candidate, 'id'));
        return Boolean(candidateKey);
      })
      .sort((a, b) => {
        const nameA = resolveComparableName(a).toLowerCase();
        const nameB = resolveComparableName(b).toLowerCase();
        return nameA.localeCompare(nameB);
      });
  };

  const updateComparableState = (
    items: ComparableEntity[],
    currentEntityKey?: string | null
  ) => {
    setComparableEntities(items);

    const visibility: VisibleEntitiesMap = {};
    const baseKey = currentEntityKey ?? deriveEntityKey(entityId);

    if (baseKey) {
      visibility[baseKey] = true;
    }

    const totalCount = items.length + (baseKey ? 1 : 0);
    if (totalCount > 3) {
      // Pick the two most recent by year (desc). Ties broken by start date (desc), then name.
      const scored = items
        .map((item) => {
          const itemKey = deriveEntityKey(get(item, 'id'));
          const year = getComparableYearNumber(item);
          const startIso = get(item, 'timeAndPlace.time.start') ?? get(item, 'startDate');
          const ts = typeof startIso === 'string' ? new Date(startIso).getTime() : 0;
          return { item, itemKey, year, ts, name: resolveComparableName(item).toLowerCase() };
        })
        .filter((x) => Boolean(x.itemKey))
        .sort((a, b) => {
          if (b.year !== a.year) return b.year - a.year;
          if (b.ts !== a.ts) return b.ts - a.ts;
          return a.name.localeCompare(b.name);
        });

      const top = scored.slice(0, 2);
      // Default visible: base + top 2; others hidden
      scored.forEach((s) => {
        if (!s.itemKey) return;
        visibility[s.itemKey] = top.some((t) => t.itemKey === s.itemKey);
      });
    } else {
      // Few enough items: show all by default
      items.forEach((item) => {
        const itemKey = deriveEntityKey(get(item, 'id'));
        if (itemKey) {
          visibility[itemKey] = true;
        }
      });
    }

    setVisibleEntities(visibility);
  };

  const fetchComparableCampaigns = async (campaign: EntityModel) => {
    const franchiseIsNamed = campaign.franchise?.editionType == FranchiseEditionType.NAMED;
    const franchiseKey = deriveEntityKey(franchiseIsNamed ? campaign.franchiseEditionId : campaign.franchiseId);
    const campaignKey = deriveEntityKey(campaign.id);
    const currentEntityKey = deriveEntityKey(campaign.id) ?? deriveEntityKey(entityId);

    if (!franchiseKey || !campaignKey) {
      updateComparableState([], currentEntityKey);
      return;
    }

    setIsLoadingComparableEntities(true);
    try {
      const session = JSON.parse(localStorage.getItem('session') || '{}');
      const endpoint = franchiseIsNamed
        ? `${config.baseApiHost}/api/v2/franchise/edition/${franchiseKey}/campaign/${campaignKey}/compare`
        : `${config.baseApiHost}/api/v2/franchise/${franchiseKey}/campaign/${campaignKey}/compare`;

      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'ApiKey': config.apikey || '',
          'Authorization': `bearer ${session.key}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch comparable campaigns');
      }

      const result = await response.json();
      const list = get(result, 'data.list', []);
      const normalized = normaliseComparableResponse(list, null);
      updateComparableState(normalized, currentEntityKey);
    } catch (error) {
      console.error('Error fetching comparable campaigns:', error);
      updateComparableState([], currentEntityKey);
    } finally {
      setIsLoadingComparableEntities(false);
    }
  };

  const fetchComparableEvents = async (eventEntity: EntityModel) => {
    const franchiseIsNamed = eventEntity.franchise?.editionType == FranchiseEditionType.NAMED;
    const franchiseKey = deriveEntityKey(franchiseIsNamed ? eventEntity.franchiseEditionId : eventEntity.franchiseId);
    const eventKey = deriveEntityKey(eventEntity.id);
    const currentEntityKey = deriveEntityKey(eventEntity.id) ?? deriveEntityKey(entityId);

    if (!franchiseKey || !eventKey) {
      updateComparableState([], currentEntityKey);
      return;
    }

    setIsLoadingComparableEntities(true);
    try {
      const session = JSON.parse(localStorage.getItem('session') || '{}');
      const endpoint = franchiseIsNamed
        ? `${config.baseApiHost}/api/v2/franchise/edition/${franchiseKey}/event/${eventKey}/compare`
        : `${config.baseApiHost}/api/v2/franchise/${franchiseKey}/event/${eventKey}/compare`;

      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'ApiKey': config.apikey || '',
          'Authorization': `bearer ${session.key}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch comparable events');
      }

      const result = await response.json();
      const list = get(result, 'data.list', []);
      const normalized = normaliseComparableResponse(list, null);
      updateComparableState(normalized, currentEntityKey);
    } catch (error) {
      console.error('Error fetching comparable events:', error);
      updateComparableState([], currentEntityKey);
    } finally {
      setIsLoadingComparableEntities(false);
    }
  };

  const calculateEntityDateRange = useCallback((entity: any, period: string) => {
    const endDate = get(entity, 'timeAndPlace.time.start') ?? get(entity, 'startDate');
    const endTime = endDate ? new Date(endDate) : new Date();

    let startTime: Date;
    switch (period) {
      case '7days':
        startTime = new Date(endTime.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '30days':
        startTime = new Date(endTime.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case 'allTime':
        // Always use 365 days before event date for comparison mode consistency
        startTime = new Date(endTime.getTime() - 365 * 24 * 60 * 60 * 1000);
        break;
      default:
        startTime = new Date(endTime.getTime() - 7 * 24 * 60 * 60 * 1000);
    }

    return {
      dateFrom: moment(startTime).format(config.DEFAULT_DATE_FORMAT),
      dateTo: moment(endTime).format(config.DEFAULT_DATE_FORMAT)
    };
  }, [entityType]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (entityId) {
      const entityConfigurations = {
        ...dashboardViewTypeConfigurations[entityType === 'event' ? 'events' : 'campaigns'],
        defaultTimeBracket: reportTimelineBracket.Default
      } as any;
      init(entityConfigurations);
      getRecord(entityId);
    }
  }, [entityId, entityType, init, getRecord]);

  useEffect(() => {
    const entityData = get(modelRecord, 'data.data', null) as EntityModel | null;
    if (!entityData || !entityData.id) {
      updateComparableState([], null);
      return;
    }

    updateComparableState([], null);

    if (entityType === 'event') {
      fetchComparableEvents(entityData);
    } else {
      fetchComparableCampaigns(entityData);
    }
  }, [modelRecord, entityType]);

  useEffect(() => {
    if (!entityId) return;

    if (urlQueryState.comparisonEnabled && !isComparisonMode) {
      setIsComparisonMode(true);
      return;
    }

    if (!urlQueryState.comparisonEnabled && isComparisonMode) {
      setIsComparisonMode(false);
    }
  }, [entityId, urlQueryState.comparisonEnabled, isComparisonMode]);

  // Sync trim state to URL when in comparison mode
  useEffect(() => {
    if (isComparisonMode && timePeriodLocal === 'allTime') {
      writeQueryParams({ trim: trimEmptyData ? true : null });
    } else if (!isComparisonMode) {
      writeQueryParams({ trim: null });
    }
  }, [isComparisonMode, trimEmptyData, timePeriodLocal, writeQueryParams]);

  useEffect(() => {
    if (!entityId) return;

    const setRange = (
      rangeKey: TimePeriodKey,
      df: Date | null,
      dt: Date | null,
      dfStr?: string,
      dtStr?: string
    ) => {
      setTimePeriodLocal(rangeKey);
      if (rangeKey === 'custom') {
        const fromStr = dfStr || (df ? moment(df).format(config.DEFAULT_DATE_FORMAT) : '');
        const toStr = dtStr || (dt ? moment(dt).format(config.DEFAULT_DATE_FORMAT) : '');
        setCustomFrom(fromStr);
        setCustomTo(toStr);
        if (fromStr && toStr) {
          onValueChange('timePeriod', { dateFrom: fromStr, dateTo: toStr });
        }
        return;
      }
      onValueChange('timePeriod', {
        dateFrom: df ? moment(df).format(config.DEFAULT_DATE_FORMAT) : null,
        dateTo: dt ? moment(dt).format(config.DEFAULT_DATE_FORMAT) : null
      });
    };

    const today = new Date();
    if (urlQueryState.periodKey === '7days') {
      const df = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      setRange('7days', df, today);
      return;
    }
    if (urlQueryState.periodKey === '30days') {
      const df = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
      setRange('30days', df, today);
      return;
    }
    if (urlQueryState.periodKey === 'allTime') {
      setTimePeriodLocal('allTime');
      return;
    }
    if (urlQueryState.periodKey === 'custom') {
      let fromStr = urlQueryState.customFromParam || '';
      let toStr = urlQueryState.customToParam || '';
      const looksIso = (d: string) => /\d{4}-\d{2}-\d{2}/.test(d) || d.includes('T');
      if (fromStr && looksIso(fromStr)) fromStr = moment(fromStr).format(config.DEFAULT_DATE_FORMAT);
      if (toStr && looksIso(toStr)) toStr = moment(toStr).format(config.DEFAULT_DATE_FORMAT);
      setRange('custom', null, null, fromStr, toStr);
      return;
    }

    const df = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    setRange('7days', df, today);
  }, [
    entityId,
    urlQueryState.periodKey,
    urlQueryState.customFromParam,
    urlQueryState.customToParam
  ]);

  useEffect(() => {
    if (!entityId) return;

    const tbFromUrl = urlQueryState.timeBracketParam;
    if (!tbFromUrl) return;

    const validTimeBrackets = [
      reportTimelineBracket.DefaultOnline,
      reportTimelineBracket.TimeToDate,
      reportTimelineBracket.TimeToDateOnline
    ];

    if (validTimeBrackets.includes(tbFromUrl) && timeBracket !== tbFromUrl) {
      onValueChange('timeBracket', tbFromUrl);
    }
  }, [entityId, urlQueryState.timeBracketParam, timeBracket, onValueChange]);

  useEffect(() => {
    setIncludeOffline(timeBracket !== reportTimelineBracket.DefaultOnline && timeBracket !== reportTimelineBracket.TimeToDateOnline);
  }, [timeBracket]);

  useEffect(() => {
    if (!urlStateReady) {
      return;
    }

    if (inited && entityId && !loadDataCalledRef.current) {
      loadDataCalledRef.current = true;
      loadData();
    }
    return () => {
      loadDataCalledRef.current = false;
    };
  }, [urlStateReady, inited, entityId, timePeriod, timeBracket, reportByDateAndMetricType, isComparisonMode]);

  const loadData = () => {
    if (!entityId) return;

    const currentTimeBracket = timeBracket || 'Default';
    const currentReportByDateAndMetricType = reportByDateAndMetricType || 'Daily';
    const currentBaseFilter = configurations?.baseFilter || `${entityType}Id`;

    let currentTimePeriod = timePeriod;
    if (isComparisonMode) {
      const entityData = get(modelRecord, 'data.data', {});
      if (!get(entityData, 'id')) {
        console.warn('Skipping performance summary fetch until comparison entity data is available', {
          entityId
        });
        return;
      }
      currentTimePeriod = calculateEntityDateRange(entityData, timePeriodLocal);
    } else if (!currentTimePeriod || !currentTimePeriod.dateFrom || !currentTimePeriod.dateTo) {
      const today = new Date();
      const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      currentTimePeriod = {
        dateFrom: moment(sevenDaysAgo).format(config.DEFAULT_DATE_FORMAT),
        dateTo: moment(today).format(config.DEFAULT_DATE_FORMAT)
      };
    }

    const dfStr = currentTimePeriod.dateFrom;
    const dtStr = currentTimePeriod.dateTo;
    const toIso = (d?: string | null) => {
      if (!d) return null;
      const looksIso = /\d{4}-\d{2}-\d{2}/.test(d) || d.includes('T');
      const jsDate = looksIso ? new Date(d) : moment(d, config.DEFAULT_DATE_FORMAT).toDate();
      return jsDate.toISOString();
    };

    const summaryFrom = toIso(dfStr);
    const summaryTo = toIso(dtStr);

    if (!summaryFrom || !summaryTo) {
      console.warn('Skipping performance summary fetch due to invalid time range', {
        entityId,
        summaryFrom,
        summaryTo
      });
      return;
    }

    getSummaryRecord(
      entityId,
      entityType,
      currentTimeBracket,
      summaryFrom,
      summaryTo,
      'summaryRecord'
    );

    try {
      const from = new Date(summaryFrom);
      const to = new Date(summaryTo);
      const durationMs = to.getTime() - from.getTime();
      const prevTo = new Date(from.getTime() - 1);
      const prevFrom = new Date(prevTo.getTime() - durationMs);
      if (timePeriodLocal !== 'allTime' && !isComparisonMode) {
        getSummaryRecord(
          entityId,
          entityType,
          currentTimeBracket,
          prevFrom.toISOString(),
          prevTo.toISOString(),
          'previousSummaryRecord'
        );
      }
    } catch (e) {
    }

    const chartRequest = dashboardActions.getSummaryDataRecordRequest(
      entityId,
      currentBaseFilter,
      currentTimePeriod,
      currentTimeBracket,
      currentReportByDateAndMetricType
    );

    getSummaryDataRecord(chartRequest);

    if (isComparisonMode && comparableEntities.length > 0) {
      loadComparisonData();
    }

    loadLeaderboardData(donationListMode);
  };

  const loadComparisonData = useCallback(async () => {
    if (!comparableEntities.length) {
      return;
    }

    setIsLoadingComparisonData(true);
    try {
      const currentTimeBracket = timeBracket || 'Default';
      const currentReportByDateAndMetricType = reportByDateAndMetricType || 'Daily';
      const currentBaseFilter = configurations?.baseFilter || `${entityType}Id`;

      const nextComparisonData: ComparisonDataRecordsMap = {};
      // Only load data for currently visible comparable entities
      const targets = comparableEntities.filter((e) => {
        const id = String(e?.id ?? '');
        return id && visibleEntities[id] !== false;
      });

      for (const compEntity of targets) {
        const comparableId = String(compEntity?.id ?? '');
        if (!comparableId) {
          continue;
        }

        try {
          const entityDateRange = calculateEntityDateRange(compEntity, timePeriodLocal);

          const chartRequest = dashboardActions.getSummaryDataRecordRequest(
            comparableId,
            currentBaseFilter,
            entityDateRange,
            currentTimeBracket,
            currentReportByDateAndMetricType
          );

          const session = JSON.parse(localStorage.getItem('session') || '{}');
          const response = await fetch(`${config.baseApiHost}/api/v3/report/day/search`, {
            method: 'POST',
            headers: {
              'ApiKey': config.apikey || '',
              'Authorization': `bearer ${session.key}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(chartRequest)
          });

          if (response.ok) {
            const result = (await response.json()) as SummaryDataApiResponse;
            nextComparisonData[comparableId] = result;
          } else {
            console.error(`Error response loading data for entity ${comparableId}:`, response.status);
          }
        } catch (error) {
          console.error(`Error loading data for entity ${comparableId}:`, error);
        }
      }

      setComparisonDataRecords(prev => {
        const updated: ComparisonDataRecordsMap = { ...prev };
        // Keep only the latest fetched for the visible set
        comparableEntities.forEach(entityCandidate => {
          const key = String(entityCandidate?.id ?? '');
          if (!key) {
            return;
          }

          if (nextComparisonData[key]) {
            updated[key] = nextComparisonData[key];
          } else {
            // Do not delete previously loaded data to allow quick toggling back on,
            // but avoid adding new entries for hidden ones.
          }
        });

        return updated;
      });
    } finally {
      setIsLoadingComparisonData(false);
    }
  }, [calculateEntityDateRange, comparableEntities, configurations?.baseFilter, entityType, reportByDateAndMetricType, timeBracket, timePeriodLocal, visibleEntities]);

  useEffect(() => {
    if (!isComparisonMode) {
      comparisonFetchSignatureRef.current = '';
    }
  }, [isComparisonMode]);

  const comparisonLoadSignature = useMemo(() => {
    if (!comparableEntities.length) {
      return '';
    }

    // Only consider visible comparable entities in the signature
    const ids = comparableEntities
      .map(candidate => String(candidate?.id ?? ''))
      .filter(id => id && visibleEntities[id] !== false)
      .sort()
      .join('|');

    if (!ids) {
      return '';
    }

    const bracketKey = timeBracket || 'Default';
    const reportKey = reportByDateAndMetricType || 'Daily';

    return [String(entityId ?? ''), ids, bracketKey, reportKey, timePeriodLocal].join('::');
  }, [comparableEntities, entityId, reportByDateAndMetricType, timeBracket, timePeriodLocal, visibleEntities]);

  useEffect(() => {
    if (!isComparisonMode) {
      return;
    }

    if (!comparableEntities.length) {
      comparisonFetchSignatureRef.current = '';
      return;
    }

    if (!comparisonLoadSignature) {
      return;
    }

    if (isLoadingComparableEntities || isLoadingComparisonData) {
      return;
    }

    // Only check for missing data among visible comparable entities
    const hasMissingData = comparableEntities.some(candidate => {
      const comparableId = String(candidate?.id ?? '');
      if (!comparableId) {
        return false;
      }

      if (visibleEntities[comparableId] === false) {
        return false;
      }

      const dataList = comparisonDataRecords[comparableId]?.data?.list;
      return !Array.isArray(dataList) || dataList.length === 0;
    });

    if (!hasMissingData) {
      comparisonFetchSignatureRef.current = comparisonLoadSignature;
      return;
    }

    if (comparisonFetchSignatureRef.current === comparisonLoadSignature) {
      return;
    }

    comparisonFetchSignatureRef.current = comparisonLoadSignature;

    loadComparisonData().catch(error => {
      console.error('Failed to load comparison data', error);
      comparisonFetchSignatureRef.current = '';
    });
  }, [
    comparableEntities,
    comparisonDataRecords,
    comparisonLoadSignature,
    isComparisonMode,
    isLoadingComparableEntities,
    isLoadingComparisonData,
    loadComparisonData,
    visibleEntities
  ]);

  const loadDonationLeaderboardData = (donationMode: 'top' | 'recent' = donationListMode) => {
    if (!entityId) return;

    const basev2Filter: ListRequestFilter = {
      key: `${entityType}Id`,
      value: entityId,
      operator: 'equalTo'
    };

    const donationOrderKey = donationMode === 'recent' ? 'donatedAtLocal' : 'amountConverted';

    const donationRequest: ListRequest = {
      filters: [basev2Filter],
      orderBy: { key: donationOrderKey, direction: 'desc' },
      pageSize: 10,
      page: 1,
      resultType: 'Full'
    };

    setIsLoadingDonations(true);
    getTopDonationsListData('dashboardTopDonations', entityId, donationRequest);
    // Clear loading state after a short delay to allow for data to be loaded
    setTimeout(() => setIsLoadingDonations(false), 500);
  };

  const loadFundraiserLeaderboardData = (fundraiserType?: string) => {
    if (!entityId) return;

    const typeToUse = fundraiserType !== undefined ? fundraiserType : selectedFundraiserType;

    const basev3Filter: ListRequestFilter = {
      key: `${entityType}Id`,
      value: [entityId],
      operator: 'equalTo'
    };

    const fundraiserFilters: ListRequestFilter[] = [
      basev3Filter,
      {
        key: 'type',
        value: typeToUse ? [typeToUse] : ([] as string[]),
        operator: 'inList'
      }
    ];

    const fundraiserRequest: ListRequest = {
      filters: fundraiserFilters,
      orderBy: { key: 'raisedAmount', direction: 'desc' },
      pageSize: 10,
      page: 1,
      resultType: 'Full'
    };

    if (typeToUse) {
      setIsLoadingFundraisers(true);
      getTopFundraisersListData('dashboardTopFundraisers', entityId, fundraiserRequest);
      // Clear loading state after a short delay to allow for data to be loaded
      setTimeout(() => setIsLoadingFundraisers(false), 500);
    }
  };

  const loadLeaderboardData = (donationMode: 'top' | 'recent' = donationListMode) => {
    loadDonationLeaderboardData(donationMode);
    loadFundraiserLeaderboardData();
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    getRecord(entityId);
    loadData();
    setTimeout(() => {
      setIsRefreshing(false);
    }, 1000);
  };

  const handleComparisonToggle = (checked: boolean) => {
    if (checked && timePeriodLocal === 'custom') {
      setIsCustomRangeOpen(false);
      handleTimePeriodChange('allTime');
    }
    setIsComparisonMode(checked);
    writeQueryParams({ comparison: checked });
  };

  const handleTrimEmptyDataToggle = useCallback((enabled: boolean) => {
    setTrimEmptyData(enabled);
    try {
      localStorage.setItem('entityPerformance.trimEmptyData', String(enabled));
    } catch {
      // localStorage not available
    }
  }, []);

  const handleTimePeriodChange = (period: string) => {
    setTimePeriodLocal(period);
    const today = new Date();
    let dateFrom: Date | null = null;
    let dateTo: Date | null = today;

    switch (period) {
      case '7days':
        dateFrom = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '30days':
        dateFrom = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case 'allTime':
        {
          const createdAt = getDashboardCreatedAt(entityType, entity);
          dateFrom = createdAt ? new Date(createdAt) : new Date(today.getTime() - 180 * 24 * 60 * 60 * 1000);
        }
        updateUrlPeriod('allTime');
        break;
      case 'custom':
        updateUrlPeriod('custom');
        setDraftCustomFrom(customFrom || '');
        setDraftCustomTo(customTo || '');
        setIsCustomRangeOpen(true);
        return;
      default:
        dateFrom = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    }

    onValueChange('timePeriod', {
      dateFrom: dateFrom ? moment(dateFrom).format(config.DEFAULT_DATE_FORMAT) : null,
      dateTo: dateTo ? moment(dateTo).format(config.DEFAULT_DATE_FORMAT) : null
    });

    if (period === '7days' || period === '30days') {
      updateUrlPeriod(period as '7days' | '30days');
    }
  };

  const handleCustomFromChange = (value: string) => {
    setDraftCustomFrom(value || '');
  };

  const handleCustomToChange = (value: string) => {
    setDraftCustomTo(value || '');
  };

  const handleCustomRangeApply = () => {
    if (!draftCustomFrom || !draftCustomTo) {
      return;
    }

    setCustomFrom(draftCustomFrom);
    setCustomTo(draftCustomTo);
    onValueChange('timePeriod', { dateFrom: draftCustomFrom, dateTo: draftCustomTo });
    updateUrlPeriod('custom', draftCustomFrom, draftCustomTo);
    setIsCustomRangeOpen(false);
  };

  const handleCustomRangeCancel = () => {
    setDraftCustomFrom(customFrom || '');
    setDraftCustomTo(customTo || '');
    setIsCustomRangeOpen(false);
    if (!customFrom || !customTo) {
      const previousPeriod = previousTimePeriodRef.current;
      setTimePeriodLocal(previousPeriod);
      updateUrlPeriod(previousPeriod);
    }
  };

  const formatDisplayDate = (value: string) => {
    if (!value) return '';
    const date = moment(value, config.DEFAULT_DATE_FORMAT, true);
    return date.isValid() ? date.format('DD MMM YYYY') : value;
  };

  const customRangeLabel = customFrom && customTo
    ? `${formatDisplayDate(customFrom)} → ${formatDisplayDate(customTo)}`
    : '';

  const handleMetricClick = (metricKey: string) => {
    setSelectedMetric(metricKey);
    onValueChange('summaryType', metricKey);
  };

  const rawEntity = get(modelRecord, 'data.data', null);
  const entity: EntityModel = rawEntity && typeof rawEntity === 'object' ? (rawEntity as EntityModel) : {};
  const summary = get(summaryRecord, 'data.data', {});
  const chartData = get(summaryDataRecord, 'data.data.list', []);
  const isTimeToDate =
    timeBracket === reportTimelineBracket.TimeToDate ||
    timeBracket === reportTimelineBracket.TimeToDateOnline;
  const ticketingEnabled = get(entity, 'ticketing.enabled') as boolean | undefined;

  // Set page title based on entity type and name
  const pageTitle = useMemo(() => {
    const entityName = typeof entity.name === 'string' && entity.name ? entity.name : '';
    const entityLabel = entityType === 'campaign' ? 'Campaign' : 'Event';
    return entityName ? `${entityLabel} ${entityName} - Performance` : `${entityLabel} Performance`;
  }, [entity.name, entityType]);

  usePageTitle(pageTitle);
  const session = useSession();

  useEffect(() => {
    const ticketMetricIds: MetricVisibilityKey[] = [
      'saleAmount',
      'numberOfSoldTickets',
      'donationAmount'
    ];
    //ignore if ticketingEnabled is undefined
    if (entityType === 'campaign' && ticketingEnabled === undefined) {
      return;
    }
    const isTicketingActive = ticketingEnabled === true;

    if (!isTicketingActive && !ticketingMetricsAutoHiddenRef.current) {
      ticketingMetricsAutoHiddenRef.current = true;

      setVisibleMetrics(prev => {
        if (!ticketMetricIds.some(id => prev[id])) {
          return prev;
        }
        return {
          ...prev,
          saleAmount: false,
          numberOfSoldTickets: false,
          donationAmount: false
        };
      });

      if (ticketMetricIds.includes(selectedMetric as MetricVisibilityKey)) {
        setSelectedMetric('raisedAmount');
        if (summaryType !== 'raisedAmount') {
          onValueChange('summaryType', 'raisedAmount');
        }
      } else if (summaryType && ticketMetricIds.includes(summaryType as MetricVisibilityKey)) {
        onValueChange('summaryType', 'raisedAmount');
      }
    }

    if (isTicketingActive && ticketingMetricsAutoHiddenRef.current) {
      ticketingMetricsAutoHiddenRef.current = false;
    }
  }, [entity, entityType, onValueChange, selectedMetric, summaryType, ticketingEnabled]);

  const giftMatches = useMemo(() => {
    const organizationTimezone = get(entity, 'organization.timezone') as string | undefined;
    const giftMatchingData = get(entity, 'giftMatching', []) as GiftMatching[];
    if (!Array.isArray(giftMatchingData)) return [] as GiftMatch[];

    const parseDateValue = (value: ListFilterPrimitive | undefined): Date | null => {
      if (typeof value === 'string' || typeof value === 'number') {
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      }
      return null;
    };
    const resolvedTimezone = organizationTimezone && moment.tz.zone(organizationTimezone)
      ? organizationTimezone
      : moment.tz.guess();
    const now = moment().tz(resolvedTimezone).toDate();

    return giftMatchingData
      .filter(gm => gm.enabled)
      .map((gm): GiftMatch => {
        const conditions = Array.isArray(gm.conditions) ? gm.conditions : [];
        const startDateTimeCondition = conditions.find(
          (c: GiftMatchingCondition) => c.key === 'dateTime' && c.operator === 'greaterOrEqualThan'
        );
        const startTime = startDateTimeCondition ? parseDateValue(startDateTimeCondition.value?.[0]) : null;
        const endDateTimeCondition = conditions.find(
          (c: GiftMatchingCondition) => c.key === 'dateTime' && c.operator === 'lesserOrEqualThan'
        );
        const endTime = endDateTimeCondition ? parseDateValue(endDateTimeCondition.value?.[0]) : null;
        const giftMatchingTotalCondition = conditions.find(
          (c: GiftMatchingCondition) => c.key === 'giftMatchingTotal' && c.operator === 'lesserOrEqualThan'
        );
        const giftMatchingTotal = giftMatchingTotalCondition ? (giftMatchingTotalCondition.value[0] as number) : null;
        const donationCountCondition = conditions.find(
          (c: GiftMatchingCondition) => c.key === 'donationCount' && c.operator === 'lesserOrEqualThan'
        );
        const donationCount = donationCountCondition ? (donationCountCondition.value[0] as number) : null;
        const campaignTotalCondition = conditions.find(
          (c: GiftMatchingCondition) => c.key === 'campaignTotal' && c.operator === 'lesserOrEqualThan'
        );
        const campaignTotal = campaignTotalCondition ? (campaignTotalCondition.value[0] as number) : null;

        let status: GiftMatch['status'] = 'complete';
        if (startTime) {
          status = now >= startTime ? 'live' : 'planned';
        }
        if (endTime) {
          status = now >= endTime ? 'complete' : status;
        }
        if (giftMatchingTotal && gm.donationMatchedTotalAmount && gm.donationMatchedTotalAmount >= giftMatchingTotal) {
          status = 'complete';
        }
        if (donationCount && gm.donationMatchedCount && gm.donationMatchedCount >= donationCount) {
          status = 'complete';
        }
        const entityRaisedAmount = typeof entity.raisedAmount === 'number' ? entity.raisedAmount : Number(entity.raisedAmount ?? 0);
        if (campaignTotal && entityRaisedAmount && entityRaisedAmount >= campaignTotal) {
          status = 'complete';
        }

        const totalRaisedValue = giftMatchingTotalCondition
          ? gm.donationMatchedTotalAmount ?? 0
          : donationCountCondition
          ? gm.donationMatchedCount ?? 0
          : campaignTotalCondition
          ? entityRaisedAmount ?? 0
          : gm.donationMatchedTotalAmount ?? 0;
        const goalValue = giftMatchingTotalCondition
          ? giftMatchingTotal ?? 0
          : donationCountCondition
          ? donationCount ?? 0
          : campaignTotalCondition
          ? campaignTotal ?? 0
          : null;
        const goalType: 'amount' | 'count' =
          giftMatchingTotalCondition || campaignTotalCondition
            ? 'amount'
            : donationCountCondition
            ? 'count'
            : 'amount';

        return {
          id: gm.id,
          name: gm.name || 'Unnamed Match',
          status,
          totalRaised: Number(totalRaisedValue) || 0,
          matchGoal: goalValue !== null ? Number(goalValue) : null,
          matchGoalType: goalType,
          startTime,
          endTime,
          multiplier: gm.multiplier || 1,
          donationMatchedTotalAmount: gm.donationMatchedTotalAmount ?? 0,
          donationMatchedCount: gm.donationMatchedCount ?? 0
        };
      });
  }, [entity]);

  const liveMatches = useMemo(() => giftMatches.filter(m => m.status === 'live'), [giftMatches]);
  const nonLiveMatches = useMemo(() => giftMatches.filter(m => m.status !== 'live'), [giftMatches]);

  const enabledFundraiserTypes = useMemo(() => {
    const types: string[] = [];
    if (get(entity, 'fundraisers.enabled')) types.push('fundraiser');
    if (get(entity, 'teams.enabled')) types.push('team');
    if (get(entity, 'collections.enabled')) types.push('collection');
    return types;
  }, [entity]);

  useEffect(() => {
    if (enabledFundraiserTypes.length > 0 && !selectedFundraiserType) {
      setSelectedFundraiserType(enabledFundraiserTypes[0]);
    }
  }, [enabledFundraiserTypes, selectedFundraiserType]);

  useEffect(() => {
    if (!entityId) return;
    if (!entity || !entity.id) return;
    if (timePeriodLocal !== 'allTime') return;
    const params = new URLSearchParams(location.search || '');
    const period = (params.get('period') || '').toLowerCase();
    if (period !== 'alltime') return;
    const createdAt = getDashboardCreatedAt(entityType, entity);
    const dt = new Date();
    const df = createdAt ? new Date(createdAt) : new Date(dt.getTime() - 180 * 24 * 60 * 60 * 1000);
    onValueChange('timePeriod', {
      dateFrom: moment(df).format(config.DEFAULT_DATE_FORMAT),
      dateTo: moment(dt).format(config.DEFAULT_DATE_FORMAT)
    });
  }, [entity.id]);

  const metricsConfig = useMemo(() => {
    const prevSummary = get(previousSummaryRecord, 'data.data', {});
    const computeDelta = (current: number, previous: number) => {
      if (timePeriodLocal === 'allTime') return { change: '—', trend: 'up' };
      if (!previous || previous === 0) return { change: '—', trend: current > 0 ? 'up' : 'flat' };
      const diff = current - previous;
      const pct = (diff / previous) * 100;
      const trend = diff >= 0 ? 'up' : 'down';
      const sign = diff >= 0 ? '+' : '';
      return { change: `${sign}${pct.toFixed(1)}%`, trend };
    };
    const timeframeLabel =
      timePeriodLocal === '30days'
        ? I18n.t('campaign.performance.metrics.vs-last-month')
        : timePeriodLocal === '7days'
        ? I18n.t('campaign.performance.metrics.vs-last-week')
        : timePeriodLocal === 'allTime'
        ? I18n.t('campaign.performance.metrics.all-time-growth')
        : timePeriodLocal === 'custom'
        ? I18n.t('campaign.performance.metrics.custom-period')
        : I18n.t('campaign.performance.metrics.vs-last-week');

    const raisedAmount = get(summary, 'raisedAmount', 0);
    const donationRaisedAmount = get(summary, 'donationRaisedAmount', 0);
    const saleRaisedAmount = get(summary, 'saleRaisedAmount', 0);
    const saleDetailCount = get(summary, 'saleDetailCount', 0);
    const teamAndFundraiserCount = get(summary, 'teamAndFundraiserCount', 0);
    const teamAndFundraiserWithDonationsCount = get(summary, 'teamAndFundraiserWithDonationsCount', 0);
    const donationCount = get(summary, 'donationCount', 0);

    const { change: raisedChange, trend: raisedTrend } = computeDelta(
      raisedAmount,
      get(prevSummary, 'raisedAmount', 0)
    );
    const { change: donAmtChange, trend: donAmtTrend } = computeDelta(
      donationRaisedAmount,
      get(prevSummary, 'donationRaisedAmount', 0)
    );
    const { change: saleAmtChange, trend: saleAmtTrend } = computeDelta(
      saleRaisedAmount,
      get(prevSummary, 'saleRaisedAmount', 0)
    );
    const { change: ticketsChange, trend: ticketsTrend } = computeDelta(
      saleDetailCount,
      get(prevSummary, 'saleDetailCount', 0)
    );
    const { change: pagesChange, trend: pagesTrend } = computeDelta(
      teamAndFundraiserCount,
      get(prevSummary, 'teamAndFundraiserCount', 0)
    );
    const { change: activePagesChange, trend: activePagesTrend } = computeDelta(
      teamAndFundraiserWithDonationsCount,
      get(prevSummary, 'teamAndFundraiserWithDonationsCount', 0)
    );
    const { change: donationsChange, trend: donationsTrend } = computeDelta(
      donationCount,
      get(prevSummary, 'donationCount', 0)
    );

    return [
      {
        id: 'raisedAmount',
        label: I18n.t('campaign.performance.metrics.total-raised'),
        value: raisedAmount,
        change: raisedChange,
        trend: raisedTrend,
        icon: DollarSign,
        accentColor: CHART_COLORS[0],
        format: 'currency',
        timeframe: timeframeLabel,
        chartColor: CHART_COLORS[0],
        chartKey: 'raisedAmount'
      },
      {
        id: 'donationAmount',
        label: I18n.t('campaign.performance.metrics.donations'),
        value: donationRaisedAmount,
        change: donAmtChange,
        trend: donAmtTrend,
        icon: Heart,
        accentColor: CHART_COLORS[1],
        format: 'currency',
        timeframe: timeframeLabel,
        chartColor: CHART_COLORS[1],
        chartKey: 'donationRaisedAmount'
      },
      {
        id: 'saleAmount',
        label: I18n.t('campaign.performance.metrics.sales'),
        value: saleRaisedAmount,
        change: saleAmtChange,
        trend: saleAmtTrend,
        icon: ShoppingCart,
        accentColor: CHART_COLORS[2],
        format: 'currency',
        timeframe: timeframeLabel,
        chartColor: CHART_COLORS[2],
        chartKey: 'saleRaisedAmount'
      },
      {
        id: 'numberOfSoldTickets',
        label: I18n.t('campaign.performance.metrics.tickets-sold'),
        value: saleDetailCount,
        change: ticketsChange,
        trend: ticketsTrend,
        icon: Ticket,
        accentColor: CHART_COLORS[3],
        format: 'number',
        timeframe: timeframeLabel,
        chartColor: CHART_COLORS[3],
        chartKey: 'saleDetailCount'
      },
      {
        id: 'numberOfActivePages',
        label: I18n.t('campaign.performance.metrics.active-pages'),
        value: teamAndFundraiserWithDonationsCount,
        change: activePagesChange,
        trend: activePagesTrend,
        icon: UserPlus,
        accentColor: CHART_COLORS[4],
        format: 'number',
        timeframe: timeframeLabel,
        chartColor: CHART_COLORS[4],
        chartKey: 'teamAndFundraiserWithDonationsCount'
      },
      {
        id: 'numberOfDonations',
        label: I18n.t('campaign.performance.metrics.total-donations'),
        value: donationCount,
        change: donationsChange,
        trend: donationsTrend,
        icon: TrendingUp,
        accentColor: CHART_COLORS[5],
        format: 'number',
        timeframe: timeframeLabel,
        chartColor: CHART_COLORS[5],
        chartKey: 'donationCount'
      },
      {
        id: 'numberOfFundraisingPages',
        label: I18n.t('campaign.performance.metrics.fundraiser-pages'),
        value: teamAndFundraiserCount,
        change: pagesChange,
        trend: pagesTrend,
        icon: Users,
        accentColor: CHART_COLORS[6],
        format: 'number',
        timeframe: timeframeLabel,
        chartColor: CHART_COLORS[6],
        chartKey: 'teamAndFundraiserCount'
      }
    ];
  }, [summary, previousSummaryRecord, timePeriodLocal]);

  const selectedMetricConfig = metricsConfig.find(m => m.id === selectedMetric);

  const chartSeries = useMemo(() => {
    if (!selectedMetricConfig) {
      return [];
    }

    if (isComparisonMode && comparableEntities.length > 0) {
      const seriesCandidates: Array<{
        id: string;
        name: string;
        color: string;
        dashedMuted?: boolean;
        year?: number;
        points: Array<{ date: Date; value: number; originalDate?: Date }>
      }> = [];

      const toRelativeDays = (dataPoints: any[], entityEndDate?: string | null) => {
        if (!entityEndDate) return [] as Array<{ dayOffset: number; date: Date; value: number }>;
        const endDate = new Date(entityEndDate);
        const endTime = endDate.getTime();
        const nowTime = Date.now();

        // Drop any points that are beyond "today" (in the future) to avoid positive offsets/leakage
        const filteredPoints = dataPoints.filter((item: any) => {
          const itemDate = new Date(item.localDate || item.dateTime || item.date);
          return itemDate.getTime() <= nowTime;
        });

        const pointsWithOffset = filteredPoints.map((item: any) => {
          const itemDate = new Date(item.localDate || item.dateTime || item.date);
          const itemTime = itemDate.getTime();
          const dayOffset = Math.round((itemTime - endTime) / (1000 * 60 * 60 * 24));

          return {
            dayOffset,
            date: itemDate,
            value: Number(get(item, selectedMetricConfig?.chartKey || selectedMetric, 0))
          };
        });

        return pointsWithOffset.sort((a, b) => a.dayOffset - b.dayOffset);
      };

      if (chartData.length) {
        const entityNameRaw = get(entity, 'franchiseEdition.name', entity.name ?? 'Current Entity');
        const entityName = typeof entityNameRaw === 'string' && entityNameRaw.trim()
          ? entityNameRaw
          : 'Current Entity';
        const entityEndDateRaw = get(entity, 'timeAndPlace.time.start') ?? get(entity, 'startDate');
        const entityEndDate = typeof entityEndDateRaw === 'string' ? entityEndDateRaw : null;
        const entityYearRaw = get(entity, 'franchiseEditionYear', get(entity, 'franchiseEdition.year', null));
        const entityYear = typeof entityYearRaw === 'number' && entityYearRaw > 0
          ? entityYearRaw
          : (entityEndDate ? new Date(entityEndDate).getFullYear() : undefined);

        const relativePoints = toRelativeDays(chartData, entityEndDate);

        seriesCandidates.push({
          id: String(entityId),
          name: entityName,
          color: getComparisonColor(0),
          year: entityYear,
          points: relativePoints.map(p => ({
            date: p.date,
            value: p.value,
            originalDate: p.date,
            dayOffset: p.dayOffset
          }))
        });
      }

      comparableEntities.forEach((compEntity, index) => {
        const compData = comparisonDataRecords[String(compEntity.id)]?.data?.list || [];
        if (!compData.length) return;

        const compNameRaw = get(compEntity, 'franchiseEdition.name', get(compEntity, 'name', `Entity ${index + 1}`));
        const compName = typeof compNameRaw === 'string' && compNameRaw.trim()
          ? compNameRaw
          : `Entity ${index + 1}`;
        const compEndDateRaw = get(compEntity, 'timeAndPlace.time.start') ?? get(compEntity, 'startDate');
        const compEndDate = typeof compEndDateRaw === 'string' ? compEndDateRaw : null;
        const compYearRaw = get(compEntity as any, 'franchiseEditionYear', get(compEntity as any, 'franchiseEdition.year', null));
        const compYear = typeof compYearRaw === 'number' && compYearRaw > 0
          ? compYearRaw
          : (compEndDate ? new Date(compEndDate).getFullYear() : undefined);

        const relativePoints = toRelativeDays(compData, compEndDate);

        seriesCandidates.push({
          id: String(compEntity.id),
          name: compName,
          color: getComparisonColor(index + 1),
          dashedMuted: true,
          year: compYear,
          points: relativePoints.map(p => ({
            date: p.date,
            value: p.value,
            originalDate: p.date,
            dayOffset: p.dayOffset
          }))
        });
      });

      const offsetDateMap = new Map<number, Date>();
      seriesCandidates.forEach(candidate => {
        candidate.points.forEach(point => {
          const offset = (point as any).dayOffset ?? 0;
          if (!offsetDateMap.has(offset)) {
            const referenceDate = point.originalDate || point.date;
            offsetDateMap.set(
              offset,
              referenceDate instanceof Date ? referenceDate : new Date(referenceDate)
            );
          }
        });
      });

      const allOffsets = Array.from(offsetDateMap.keys()).sort((a, b) => a - b);
      
      // Find first non-zero data point for each series
      const firstNonZeroOffsets: number[] = [];
      seriesCandidates.forEach((candidate) => {
        const nonZeroPoints = candidate.points.filter(p => p.value > 0);
        const firstNonZero = nonZeroPoints.length > 0 
          ? Math.min(...nonZeroPoints.map(p => (p as any).dayOffset))
          : null;
        if (firstNonZero !== null) {
          firstNonZeroOffsets.push(firstNonZero);
        }
      });
      
      // Normalize offsets to start from 0
      // In all-time comparison mode, optionally trim to the earliest non-zero data point to avoid empty leading space
      let minOffset = allOffsets.length > 0 ? allOffsets[0] : 0;
      
      if (trimEmptyData && timePeriodLocal === 'allTime' && isComparisonMode && firstNonZeroOffsets.length > 0) {
        const earliestNonZero = Math.min(...firstNonZeroOffsets);
        // Add 14-day buffer before the first non-zero data point for visual context
        const trimmedOffset = Math.max(earliestNonZero - 5, allOffsets[0]);
        minOffset = trimmedOffset;
      }
      
      const normalizedOffsets = allOffsets.map(offset => offset - minOffset);
      
      // Create a mapping from normalized offset back to original offset
      const originalOffsetMap = new Map<number, number>();
      allOffsets.forEach((originalOffset, index) => {
        originalOffsetMap.set(normalizedOffsets[index], originalOffset);
      });

      // Map each candidate's existing points to a shared normalized offset, without adding synthetic zeros.
      const allSeries = seriesCandidates.map(candidate => {
        // In all-time comparison mode with smart trimming enabled, filter out points before the trimmed minOffset
        const pointsToInclude = (trimEmptyData && timePeriodLocal === 'allTime' && isComparisonMode)
          ? candidate.points.filter(point => {
              const offset = (point as any).dayOffset ?? 0;
              return offset >= minOffset;
            })
          : candidate.points;
        
        const normalizedPoints = pointsToInclude.map(point => {
          const originalOffset = (point as any).dayOffset ?? 0;
          const normalizedOffset = originalOffset - minOffset;
          return {
            ...point,
            dayOffset: normalizedOffset,
            originalDayOffset: originalOffset
          };
        });

        return {
          id: candidate.id,
          name: candidate.name,
          color: candidate.color,
          dashedMuted: candidate.dashedMuted,
          year: (candidate as any).year,
          data: normalizedPoints,
          valueFormatter: (value: number) =>
            selectedMetricConfig.format === 'currency'
              ? formatCurrency(value)
              : formatNumber(value),
          aggregationStrategy: (isTimeToDate ? 'latest' : 'sum') as 'latest' | 'sum'
        };
      });

  const visibleSeries = allSeries.filter(series => visibleEntities[series.id] !== false && series.data.length > 0);

      return visibleSeries.sort((a, b) => {
        // Current campaign always comes first
        if (a.id === entityId) return -1;
        if (b.id === entityId) return 1;
        
        // Sort by year descending (newest first)
        const yearA = a.year || 0;
        const yearB = b.year || 0;
        
        if (yearB !== yearA) {
          return yearB - yearA;
        }
        
        // Fallback to alphabetical by name if years are the same
        return a.name.localeCompare(b.name);
      });
    }

    if (!chartData.length) {
      return [];
    }

    const points = chartData.map((item: any) => ({
      date: new Date(item.localDate || item.dateTime || item.date),
      value: Number(get(item, selectedMetricConfig?.chartKey || selectedMetric, 0))
    }));

    return [
      {
        id: 'primary',
        name: selectedMetricConfig.label,
        color: selectedMetricConfig.chartColor,
        data: points,
        valueFormatter: (value: number) =>
          selectedMetricConfig.format === 'currency'
            ? formatCurrency(value)
            : formatNumber(value),
        aggregationStrategy: (isTimeToDate ? 'latest' : 'sum') as 'latest' | 'sum'
      }
    ];
  }, [
    chartData,
    selectedMetric,
    selectedMetricConfig,
    isTimeToDate,
    isComparisonMode,
    comparableEntities,
    comparisonDataRecords,
    visibleEntities,
    entityId,
    trimEmptyData,
    timePeriodLocal
  ]);

  const comparisonLegendItems = useMemo(() => {
    if (!isComparisonMode) {
      return [] as Array<{ id: string; name: string; color: string; year?: number }>;
    }

    const legendEntries: Array<{ id: string; name: string; color: string; year?: number }> = [];

    if (chartData.length) {
      const entityData = get(modelRecord, 'data.data', {});
      const isNamed = get(entityData, 'franchise.editionType') === FranchiseEditionType.NAMED;
      const entityEndDateRaw = get(entityData, 'timeAndPlace.time.start') ?? get(entityData, 'startDate');
      const entityEndDate = typeof entityEndDateRaw === 'string' ? entityEndDateRaw : null;
      const entityYearRaw = get(entityData, 'franchiseEditionYear', get(entityData, 'franchiseEdition.year', null));
      const entityYear = typeof entityYearRaw === 'number' && entityYearRaw > 0
        ? entityYearRaw
        : (entityEndDate ? new Date(entityEndDate).getFullYear() : undefined);
      
      let entityName: string;
      if (isNamed && entityYear) {
        const editionName = get(entityData, 'franchiseEdition.name', '');
        const editionNameStr = typeof editionName === 'string' ? editionName : '';
        entityName = `${editionNameStr} ${entityYear}`;
      } else {
        entityName = get(entityData, 'franchiseEdition.name', get(entityData, 'name', 'Current Entity'));
      }

      legendEntries.push({
        id: String(entityId),
        name: entityName,
        color: getComparisonColor(0),
        year: entityYear
      });
    }

    // Show all comparable entities in the legend so users can toggle them on to load data on demand
    comparableEntities.forEach((compEntity, index) => {
      const isNamed = get(compEntity, 'franchise.editionType') === FranchiseEditionType.NAMED;
      const compEndDateRaw = get(compEntity, 'timeAndPlace.time.start') ?? get(compEntity, 'startDate');
      const compEndDate = typeof compEndDateRaw === 'string' ? compEndDateRaw : null;
      const compYearRaw = get(compEntity as any, 'franchiseEditionYear', get(compEntity as any, 'franchiseEdition.year', null));
      const compYear = typeof compYearRaw === 'number' && compYearRaw > 0
        ? compYearRaw
        : (compEndDate ? new Date(compEndDate).getFullYear() : undefined);
      
      let compName: string;
      if (isNamed && compYear) {
        const editionName = get(compEntity, 'franchiseEdition.name', '');
        const editionNameStr = typeof editionName === 'string' ? editionName : '';
        compName = `${editionNameStr} ${compYear}`;
      } else {
        const compNameRaw = get(compEntity, 'franchiseEdition.name', get(compEntity, 'name', `Entity ${index + 1}`));
        compName = typeof compNameRaw === 'string' && compNameRaw.trim()
          ? compNameRaw
          : `Entity ${index + 1}`;
      }

      legendEntries.push({
        id: String(compEntity.id),
        name: compName,
        color: getComparisonColor(index + 1),
        year: compYear
      });
    });

    return legendEntries.sort((a, b) => {
      // Current campaign always comes first
      if (a.id === entityId) return -1;
      if (b.id === entityId) return 1;
      
      // Sort by year descending (newest first)
      const yearA = a.year || 0;
      const yearB = b.year || 0;
      
      if (yearB !== yearA) {
        return yearB - yearA;
      }
      
      // Fallback to alphabetical by name if years are the same
      return a.name.localeCompare(b.name);
    });
  }, [
    isComparisonMode,
    chartData,
    comparableEntities,
    comparisonDataRecords,
    entityId,
    modelRecord
  ]);

  const isSummaryLoading = Record.isRecordLoading(summaryRecord);
  const isChartLoading = Record.isRecordLoading(summaryDataRecord);

  if (!inited) {
    return <Spinner />;
  }

  if (!entityId) {
    return <div>No entity ID provided</div>;
  }

  const name = typeof entity.name === 'string' ? entity.name : '';
  // System admins see the organisation after a campaign name, as its own marked detail.
  const organisationName =
    name && entityType === 'campaign' && isSystemAdmin &&
    entity.organization && typeof entity.organization.name === 'string'
      ? entity.organization.name
      : '';

  const editUrl = entityType === 'campaign'
    ? `/campaigns/edit/${entityId}/setup`
    : `/events/edit/${entityId}/setup`;
  const visitUrlRaw = typeof entity.urlFull === 'string' && entity.urlFull.trim()
    ? entity.urlFull
    : typeof entity.url === 'string'
    ? entity.url
    : '';
  const visitUrl = visitUrlRaw.trim();
  const canVisitEntity = Boolean(visitUrl);
  const showCheckIn =
    entityType === 'campaign' &&
    Boolean(get(entity, 'ticketing.enabled')) &&
    canUseCheckIn(session);
  // The check-in page only loads live campaigns
  const isCheckInOpen = entity.status === entityStatus.live;
  const checkInUrl = CONTEXT_MENU_KEYS.CAMPAIGN_MENU.CHECK_IN.getUrl(
    entityId,
    null
  );
  const canOpenCheckIn = showCheckIn && isCheckInOpen;

  const handleCopyPageUrl = async () => {
    const pageUrl = typeof entity.urlFull === 'string' && entity.urlFull.trim()
      ? entity.urlFull.trim()
      : typeof entity.url === 'string'
      ? entity.url.trim()
      : '';
    if (!pageUrl) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(pageUrl);
      } else {
        const tempInput = document.createElement('input');
        tempInput.value = pageUrl;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand('copy');
        document.body.removeChild(tempInput);
      }
    } catch (error) {
      console.error('Failed to copy performance URL', error);
    }
  };
  const handleVisitEntity = () => {
    if (!visitUrl) return;
    window.open(visitUrl, '_blank', 'noopener,noreferrer');
  };

  const legacyUrlbase = entityType === 'campaign' ? '/campaigns' : '/events';

  // Edit action - shown as primary button (hidden for EventAdmin/EventOrganizerAdmin on events)
  const shouldHideEditAction = entityType === 'event' && (isEventAdmin || isEventOrganizerAdmin);
  const editAction: EntityActionsMenuItem | undefined = shouldHideEditAction
    ? undefined
    : {
        key: 'edit',
        label: I18n.t(
          entityType === 'campaign'
            ? 'campaign.performance.header.edit-campaign'
            : 'campaign.performance.header.edit-event'
        ),
        icon: <Edit3 aria-hidden="true" />,
        linkTo: editUrl
      };

  // Campaigns get the share modal in place of the plain copy action; events keep copy
  const showCampaignLinksModal = entityType === 'campaign';

  // Build other action menu items
  const actionMenuItems: EntityActionsMenuItem[] = [
    showCampaignLinksModal
      ? {
          key: 'campaign-links',
          label: I18n.t('campaign.performance.header.campaign-links'),
          icon: <QrCode aria-hidden="true" />,
          onSelect: () => setCampaignLinksModalOpen(true),
          disabled: !entity.urlFull
        }
      : {
          key: 'copy',
          label: I18n.t('campaign.performance.header.copy-link'),
          icon: <Copy aria-hidden="true" />,
          onSelect: handleCopyPageUrl
        },
    {
      key: 'visit',
      label: I18n.t('campaign.performance.header.visit'),
      icon: <ExternalLink aria-hidden="true" />,
      onSelect: handleVisitEntity,
      disabled: !canVisitEntity
    }
  ];

  if (showCheckIn) {
    actionMenuItems.push({
      key: CONTEXT_MENU_KEYS.CAMPAIGN_MENU.CHECK_IN.key,
      label: I18n.t('campaign.performance.header.check-in'),
      icon: <UserCheck aria-hidden="true" />,
      linkTo: checkInUrl,
      disabled: !isCheckInOpen,
      tooltip: isCheckInOpen
        ? undefined
        : I18n.t('campaign.performance.header.check-in-closed')
    });
  }

  if (entity.franchiseId) {
    actionMenuItems.push({
      key: CONTEXT_MENU_KEYS.FRANCHISES.HUB.key,
      label: 'Hub',
      icon: <ExternalLink aria-hidden="true" />,
      linkTo: CONTEXT_MENU_KEYS.FRANCHISES.HUB.getUrl({ id: entity.franchiseId }, null)
    });
  }

  // Audit trail link for system admins
  if (isSystemAdmin) {
    const auditParam = entityType === 'campaign' ? 'targetCampaignId' : 'targetEventId';
    actionMenuItems.push({
      key: CONTEXT_MENU_KEYS.CAMPAIGN_MENU.AUDIT_TRAIL.key,
      label: I18n.t('campaign.performance.header.audit-trail'),
      icon: <History aria-hidden="true" />,
      linkTo: CONTEXT_MENU_KEYS.CAMPAIGN_MENU.AUDIT_TRAIL.getUrl({ id: entityId }, { paramName: auditParam }),
      systemOnly: true
    });
  }

  actionMenuItems.push({
    key: 'legacy-dashboard',
    label: I18n.t('campaign.performance.header.legacy-dashboard'),
    icon: <LayoutDashboard aria-hidden="true" />,
    linkTo: `${legacyUrlbase}/dashboard/${entityId}`
  });

  return (
    <div className={styles.container}>
      <BasePage>
        <div className={styles.main}>
          <div className={styles.header}>
            <div className={styles.headerContent}>
              <h1>
                {name}
                {organisationName && (
                  <SystemOnlyDetail>{organisationName}</SystemOnlyDetail>
                )}
              </h1>
              <EntityTimestamps entity={entity} />
              <p>{I18n.t('campaign.performance.subtitle')}.</p>
            </div>
            <div className={styles.headerActions}>
              <button
                onClick={handleRefresh}
                className={styles.refreshButton}
                disabled={isRefreshing}
              >
                <RefreshCw className={`${styles.icon} ${isRefreshing ? styles.iconSpinning : ''}`} />
              </button>
              <EntityHeaderActions
                className={styles.actionsDropdown}
                editAction={editAction}
                menuItems={actionMenuItems}
                actionsLabel={I18n.t('campaign.performance.header.actions')}
              />
            </div>
          </div>

          {liveMatches.length > 0 && (
            <GiftMatchWidget
              matches={liveMatches}
              selectedIndex={selectedLiveIndex}
              onSelectIndex={setSelectedLiveIndex}
              formatCurrency={renderCurrencyCompact}
            />
          )}

          <div className={styles.metricsSection}>
            <div className={styles.metricsHeader}>
              <h2>{I18n.t('campaign.performance.metrics.title')}</h2>
              <div className={styles.metricsControls}>
                <div className={styles.timePeriodToggle}>
                  <button
                    onClick={() => handleTimePeriodChange('7days')}
                    className={`${styles.timePeriodButton} ${
                      timePeriodLocal === '7days' ? styles.timePeriodButtonActive : ''
                    }`}
                  >
                    {I18n.t('campaign.performance.metrics.time-periods.7days')}
                  </button>
                  <button
                    onClick={() => handleTimePeriodChange('30days')}
                    className={`${styles.timePeriodButton} ${
                      timePeriodLocal === '30days' ? styles.timePeriodButtonActive : ''
                    }`}
                  >
                    {I18n.t('campaign.performance.metrics.time-periods.30days')}
                  </button>
                  <button
                    onClick={() => handleTimePeriodChange('allTime')}
                    className={`${styles.timePeriodButton} ${
                      timePeriodLocal === 'allTime' ? styles.timePeriodButtonActive : ''
                    }`}
                  >
                    {I18n.t('campaign.performance.metrics.time-periods.all-time')}
                  </button>
                  {!isComparisonMode && (
                    <button
                      onClick={() => handleTimePeriodChange('custom')}
                      className={`${styles.timePeriodButton} ${
                        timePeriodLocal === 'custom' ? styles.timePeriodButtonActive : ''
                      }`}
                    >
                      {I18n.t('campaign.performance.metrics.time-periods.custom')}
                    </button>
                  )}
                </div>
                {!isComparisonMode && timePeriodLocal === 'custom' && (
                  <div className={styles.customRangeWrapper} ref={customRangeRef}>
                    <button
                      type="button"
                      className={`${styles.customRangeButton} ${isCustomRangeOpen ? styles.customRangeButtonActive : ''}`}
                      onClick={() => {
                        if (!isCustomRangeOpen) {
                          setDraftCustomFrom(customFrom || '');
                          setDraftCustomTo(customTo || '');
                        }
                        setIsCustomRangeOpen(prev => !prev);
                      }}
                      aria-haspopup="dialog"
                      aria-expanded={isCustomRangeOpen}
                      aria-label={I18n.t('campaign.performance.metrics.time-periods.custom')}
                    >
                      <Calendar className={styles.icon} />
                      <span className={styles.customRangeDisplay}>
                        {customRangeLabel ? (
                          customRangeLabel
                        ) : (
                          <span className={styles.customRangePlaceholder}>{I18n.t('campaign.performance.metrics.custom-range.select')}</span>
                        )}
                      </span>
                      <ChevronDown
                        className={`${styles.icon} ${styles.customRangeChevron} ${isCustomRangeOpen ? styles.customRangeChevronOpen : ''}`}
                      />
                    </button>
                    {isCustomRangeOpen && (
                      <div className={styles.customRangePopover} role="dialog" aria-modal="true">
                        <div className={styles.customRangePopoverInner}>
                          <div className={styles.customRangeInputs}>
                            <DatePickerInput
                              id="customFrom"
                              className={styles.customRangeInput}
                              label={I18n.t('dashboard.compare.builder.form.dateFrom')}
                              value={draftCustomFrom}
                              onChange={handleCustomFromChange}
                            />
                            <DatePickerInput
                              id="customTo"
                              className={styles.customRangeInput}
                              label={I18n.t('dashboard.compare.builder.form.dateTo')}
                              value={draftCustomTo}
                              onChange={handleCustomToChange}
                            />
                          </div>
                          <div className={styles.customRangeActions}>
                            <button
                              type="button"
                              className={styles.customRangeActionButton}
                              onClick={handleCustomRangeCancel}
                            >
                              {I18n.t('campaign.performance.metrics.custom-range.cancel')}
                            </button>
                            <button
                              type="button"
                              className={`${styles.customRangeActionButton} ${styles.customRangeActionPrimary}`}
                              onClick={handleCustomRangeApply}
                              disabled={!draftCustomFrom || !draftCustomTo}
                            >
                              {I18n.t('campaign.performance.metrics.custom-range.apply')}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {!includeOffline && (
                  <span className={styles.onlineOnlyIndicator}>
                    {I18n.t('campaign.performance.metrics.settings.online-only')}
                  </span>
                )}

                <div ref={settingsRef} className={styles.settingsControl}>
                  <button
                    type="button"
                    onClick={() => setShowMetricSettings(!showMetricSettings)}
                    className={`${styles.settingsButton} ${showMetricSettings ? styles.settingsButtonActive : ''}`}
                    aria-haspopup="menu"
                    aria-expanded={showMetricSettings}
                  >
                    <Settings className={styles.icon} aria-hidden="true" />
                  </button>
                  {showMetricSettings && (
                    <div className={styles.settingsDropdown}>
                      <p>{I18n.t('campaign.performance.metrics.settings.title')}</p>
                      {metricsConfig.map(metric => (
                        <label key={metric.id} className={styles.settingsOption}>
                          <input
                            id={`metric-toggle-${metric.id}`}
                            type="checkbox"
                            checked={visibleMetrics[metric.id as keyof typeof visibleMetrics]}
                            onChange={(e) => setVisibleMetrics({
                              ...visibleMetrics,
                              [metric.id]: e.target.checked
                            })}
                          />
                          <span>{metric.label}</span>
                        </label>
                      ))}
                      <hr style={{ border: 'none', borderTop: '1px solid var(--border-primary)', margin: '8px 0' }} />
                      <label className={styles.settingsOption}>
                        <input
                          type="checkbox"
                          checked={timeBracket === reportTimelineBracket.TimeToDate || timeBracket === reportTimelineBracket.TimeToDateOnline}
                          onChange={(e) => {
                            const offline = includeOffline;
                            const nextTb = e.target.checked
                              ? (offline ? reportTimelineBracket.TimeToDate : reportTimelineBracket.TimeToDateOnline)
                              : (offline ? reportTimelineBracket.Default : reportTimelineBracket.DefaultOnline);
                            onValueChange('timeBracket', nextTb);
                            setIncludeOffline(offline);
                            updateUrlTimeBracket(nextTb);
                          }}
                        />
                        <span>Time to date</span>
                      </label>
                      <hr style={{ border: 'none', borderTop: '1px solid var(--border-primary)', margin: '8px 0' }} />
                      <label className={styles.settingsOption}>
                        <input
                          type="checkbox"
                          checked={timeBracket !== reportTimelineBracket.DefaultOnline && timeBracket !== reportTimelineBracket.TimeToDateOnline}
                          onChange={(e) => {
                            const isDefaultFamily =
                              timeBracket === reportTimelineBracket.Default ||
                              timeBracket === reportTimelineBracket.DefaultOnline ||
                              !timeBracket;
                            const offline = isDefaultFamily
                              ? reportTimelineBracket.Default
                              : reportTimelineBracket.TimeToDate;
                            const online = isDefaultFamily
                              ? reportTimelineBracket.DefaultOnline
                              : reportTimelineBracket.TimeToDateOnline;
                            const nextTb = e.target.checked ? offline : online;
                            onValueChange('timeBracket', nextTb);
                            setIncludeOffline(
                              nextTb !== reportTimelineBracket.DefaultOnline &&
                              nextTb !== reportTimelineBracket.TimeToDateOnline
                            );
                            updateUrlTimeBracket(nextTb);
                          }}
                        />
                        <span>{I18n.t('campaign.performance.metrics.settings.include-offline')}</span>
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.metricsGrid}>
              {isSummaryLoading && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <Spinner />
                </div>
              )}
              {!isSummaryLoading && metricsConfig.filter(m => visibleMetrics[m.id as keyof typeof visibleMetrics]).map((metric) => {
                const isActive = selectedMetric === metric.id;

                return (
                  <MetricCard
                    key={metric.id}
                    id={metric.id}
                    label={metric.label}
                    value={metric.value}
                    change={metric.change}
                    trend={metric.trend as 'up' | 'down' | 'flat'}
                    timeframe={metric.timeframe}
                    icon={metric.icon}
                    accentColor={metric.accentColor}
                    format={metric.format as 'currency' | 'number'}
                    isActive={isActive}
                    onClick={handleMetricClick}
                    formatCurrency={formatCurrency}
                    formatNumber={formatNumber}
                  />
                );
              })}
            </div>
          </div>

          <div className={styles.chartSection}>
            <div className={styles.chartContainer}>
              <div className={styles.chartHeader}>
                <h3>
                  {selectedMetricConfig?.label} {I18n.t('campaign.performance.chart.title')}
                </h3>
                <p>
                  {timePeriodLocal === '7days' && I18n.t('campaign.performance.chart.time-labels.last-7-days')}
                  {timePeriodLocal === '30days' && I18n.t('campaign.performance.chart.time-labels.last-30-days')}
                  {timePeriodLocal === 'allTime' && I18n.t('campaign.performance.chart.time-labels.all-time')}
                  {timePeriodLocal === 'custom' && I18n.t('campaign.performance.chart.time-labels.custom-range')}
                </p>
              </div>

              {comparableEntities.length > 0 && (
                <ComparisonControls
                  isComparisonMode={isComparisonMode}
                  onComparisonToggle={handleComparisonToggle}
                  trimEmptyData={trimEmptyData}
                  onTrimEmptyDataToggle={handleTrimEmptyDataToggle}
                  comparableEntities={comparisonLegendItems.map(item => ({
                    id: item.id,
                    name: item.name,
                    year: item.year,
                    color: item.color
                  }))}
                  visibleEntities={visibleEntities}
                  onEntityVisibilityChange={(entityId, visible) => {
                    setVisibleEntities(prev => ({
                      ...prev,
                      [entityId]: visible
                    }));
                  }}
                  maxVisibleItems={5}
                />
              )}

              <div className={styles.chartWrapper}>
                {isChartLoading || isLoadingComparisonData ? (
                  <Spinner />
                ) : chartSeries.length > 0 ? (
                  <AnalyticsLineChart
                    series={chartSeries}
                    isLoading={false}
                    noDataMessage={I18n.t('dashboard.top-area.chart.no-items')}
                    legend={{ show: false, truncateAfter: 30, maxItems: 6 }}
                    headerSummary={{ show: false, mode: 'single' }}
                    time={{
                      alignMode: isComparisonMode ? 'relativeStart' : 'absolute',
                      granularity: 'daily',
                      autoAggregate: true,
                      axisType: 'date',
                      relativePeriod: isComparisonMode && timePeriodLocal === 'allTime' ? 'allTime' : 'short',
                      customLabelFormatter: isComparisonMode
                        ? (date: Date, dayOffset?: number) => {
                            if (dayOffset === undefined) return '';
                            if (dayOffset === 0) return 'Event date';
                            return `T - ${Math.abs(dayOffset)}D`;
                          }
                        : undefined
                    }}
                    performance={{
                      bullets: isComparisonMode ? 'never' : 'always',
                      maxSeries: isComparisonMode ? 6 : 1,
                      tensionX: 0.8,
                      tensionY: 0.8
                    }}
                    height={320}
                    yAxisLabel={selectedMetricConfig?.label}
                  />
                ) : (
                  <div className={styles.chartPlaceholder}>
                    <p>{I18n.t('dashboard.top-area.chart.no-items')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {nonLiveMatches.length > 0 && (
            <GiftMatchWidget
              matches={nonLiveMatches}
              selectedIndex={selectedNonLiveIndex}
              onSelectIndex={setSelectedNonLiveIndex}
              formatCurrency={renderCurrencyCompact}
            />
          )}

          <TicketingSalesWidget
            data={ticketingSales}
            checkInUrl={canOpenCheckIn ? checkInUrl : undefined}
          />

          <div className={`${styles.bottomWidgets} ${styles.bottomWidgetsTwoCol}`}>
            {enabledFundraiserTypes.length > 0 && (
              <FundraiserListWidget
                title={I18n.t('campaign.performance.leaderboards.top-fundraisers')}
                fundraisers={topFundraisersList || []}
                selectedType={selectedFundraiserType}
                enabledTypes={enabledFundraiserTypes}
                onTypeChange={(type) => {
                  setSelectedFundraiserType(type);
                  loadFundraiserLeaderboardData(type);
                }}
                maxItems={10}
                viewAllLink={`/${entityType}s/view/fundraisers/${entityId}`}
                viewAllText={I18n.t('campaign.performance.leaderboards.view-all')}
                formatFundraiserType={formatFundraiserType}
                isLoading={isLoadingFundraisers}
              />
            )}

            <DonationListWidget
              title={I18n.t('campaign.performance.leaderboards.top-donations')}
              donations={topDonationsList || []}
              mode={donationListMode}
              onModeChange={(mode) => {
                setDonationListMode(mode);
                loadDonationLeaderboardData(mode);
              }}
              maxItems={10}
              viewAllLink={`/${entityType}s/view/donations/${entityId}`}
              viewAllText={I18n.t('campaign.performance.leaderboards.view-all')}
              formatTimeElapsed={formatTimeElapsedOrDate}
              modeToggleLabels={{
                top: I18n.t('campaign.performance.leaderboards.donations-toggle.top'),
                recent: I18n.t('campaign.performance.leaderboards.donations-toggle.recent')
              }}
              isLoading={isLoadingDonations}
            />
          </div>
        </div>
      </BasePage>
      {showCampaignLinksModal && (
        <CampaignLinksModal
          open={campaignLinksModalOpen}
          entity={entity}
          onClose={() => setCampaignLinksModalOpen(false)}
        />
      )}
    </div>
  );
};

export default EntityPerformancePage;
